import React, { useCallback, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import Arrow from "../../../Static/RawImages/arrow.png";
import NoRecord from "../../../Components/NoRecord";
import Pagination from "../../Components/pagination/Pagination";
import usePagination from "../../../hooks/usePagination";
import { showToast } from "../../../utils/handleToast";
import { getCase, getInvoicesByCaseId, getInvoiceBranches } from "../../../API/litigationApis";
import { getClients } from "../../../apis/invoiceApis";
import InvoiceDetailsModal from "../../Components/Litigation/InvoiceDetailsModal";
import FilterButton from "../../../ui/buttons/FilterButton";
import AssignmentsFilterModal from "../../Components/Litigation/AssignmentsFilterModal";
import useFilterPersistence from "../../../hooks/useFilterPersistence";
import useAutoRefresh from "../../../hooks/useAutoRefresh";

// API for updating invoice numbers
const updateInvoiceNumberApi = async (caseId, invoiceNumbers) => {
  try {
    const formData = new FormData();
    formData.append('id', caseId);
    
    // Append each invoice number as separate form field
    invoiceNumbers.forEach((number, index) => {
      formData.append(`invoice_number`, number);
    });

    const response = await fetch(`https://api.themedius.ai/litigation/api/update-case-invoice-numbers/`, {
      method: "POST",
      body: formData,
    });
    if (response.ok) {
      return await response.json();
    }
    return { status: false };
  } catch (error) {
    console.error("Error updating invoice numbers:", error);
    return { status: false };
  }
};

const INITIAL_FILTERS = {
  client_id: "",
  branch_name: "",
  invoice_number: "",
  lan_number: "",
  customer_name: "",
  assignment_from_date: "",
  assignment_to_date: "",
  assignment_type: "",
  invoice_status: "",
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

export default function ManageAssignments() {
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentSummary, setAssignmentSummary] = useState({
    count: 0,
    invoice_raised: 0,
    invoice_pending: 0,
    invoice_amount_raised: 0,
    invoice_amount_paid: 0,
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const { filters, setFilters: updateFilters, updateFilter, clearFilters, hasActiveFilters } = useFilterPersistence("assignment", INITIAL_FILTERS);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [invoicesList, setInvoicesList] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [updateInvoiceModal, setUpdateInvoiceModal] = useState(false);
  const [selectedAssignmentForUpdate, setSelectedAssignmentForUpdate] = useState(null);
  const [newInvoiceNumbers, setNewInvoiceNumbers] = useState("");
  const [updatingInvoice, setUpdatingInvoice] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [loadingAssignmentTypes, setLoadingAssignmentTypes] = useState(false);
  const [showRaiseInvoiceModal, setShowRaiseInvoiceModal] = useState(false);
  const [selectedAssignmentForRaise, setSelectedAssignmentForRaise] = useState(null);
  const [raisingInvoice, setRaisingInvoice] = useState(false);
  const [invoiceClients, setInvoiceClients] = useState([]);
  const [invoiceClientsLoading, setInvoiceClientsLoading] = useState(false);
  const [invoiceBranchOptions, setInvoiceBranchOptions] = useState([]);
  const [invoiceBranchesLoading, setInvoiceBranchesLoading] = useState(false);

  const {
    currentPage,
    total,
    handleTotal,
    paginationProps,
    resetPagination,
  } = usePagination({ defaultLimit: 20 });

  // Fetch assignment types on component mount
  useEffect(() => {
    const fetchAssignmentTypes = async () => {
      setLoadingAssignmentTypes(true);
      try {
        const response = await fetch('http://localhost:8000/litigation/api/get-assignment-types/');
        console.log('Assignment types response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Assignment types API response:', data);
          if (data.status === true && Array.isArray(data.data)) {
            console.log('Setting assignment types:', data.data);
            setAssignmentTypes(data.data);
          } else {
            
            // Fallback to default types
            setAssignmentTypes(["Legal Audit Report", "Legal Vetting Report", "litigation", "Title Opinion Report", "Title Search Report"]);
          }
        } else {
       ;
          // Fallback to default types
          setAssignmentTypes(["Legal Audit Report", "Legal Vetting Report", "litigation", "Title Opinion Report", "Title Search Report"]);
        }
      } catch (error) {
        console.error('Error fetching assignment types:', error);
        // Fallback to default types
        setAssignmentTypes(["Legal Audit Report", "Legal Vetting Report", "litigation", "Title Opinion Report", "Title Search Report"]);
      } finally {
        setLoadingAssignmentTypes(false);
      }
    };
    fetchAssignmentTypes();
  }, []);

  const fetchAssignments = useCallback(async (pageNum = 1) => {
    setLoading(true);

    try {
      const params = {};

      if (filters.client_id) {
        params.client_id = filters.client_id;
      }

      if (filters.branch_name) {
        params.branch_name = filters.branch_name;
      }

      if (filters.invoice_number) {
        params.invoice_no = filters.invoice_number;
      }

      if (filters.lan) {
        params.lan_number = filters.lan;
      }

      if (filters.customer_name) {
        params.customer_name = filters.customer_name;
      }

      if (filters.assignment_from_date) {
        params.assignment_from_date = filters.assignment_from_date;
      }

      if (filters.assignment_to_date) {
        params.assignment_to_date = filters.assignment_to_date;
      }

      if (filters.assignment_type) {
        params.assignment_type = filters.assignment_type;
        
      }

      if (filters.invoice_status) {
        params.invoice_status = filters.invoice_status;
      }

      console.log("Assignment API params being sent:", params);
      params.page = pageNum;
      params.page_size = paginationProps.limitPerPage;
      const response = await getCase(params);
      console.log("getCase raw response:", response);

      // Handle API/network errors returned from `getCase` (helpers return the Error object on failure)
      if (!response || response instanceof Error || response?.status === false) {
        console.error("getCase API error:", response);
        showToast({ variant: "error", message: `Failed to fetch assignments: ${response?.message || "Server error"}` });
        setAssignments([]);
        setAssignmentSummary({
          count: 0,
          invoice_raised: 0,
          invoice_pending: 0,
          invoice_amount_raised: 0,
          invoice_amount_paid: 0,
        });
        handleTotal(0);
        return;
      }

      const extractResults = (resp) => {
        if (!resp) return [];
        if (Array.isArray(resp.data?.results)) return resp.data.results;
        if (resp.data?.results && resp.data.results.data && Array.isArray(resp.data.results.data)) return resp.data.results.data;
        if (Array.isArray(resp.results)) return resp.results;
        if (Array.isArray(resp.data)) return resp.data;
        return [];
      };

      const normalizedRows = extractResults(response);
      console.log("normalizedRows length:", normalizedRows.length);
      setAssignments(normalizedRows);

      const responseData = response?.data || {};
      const summary = responseData?.summary || response?.summary || {};
      setAssignmentSummary({
        count: responseData?.count ?? response?.count ?? normalizedRows.length,
        invoice_raised: summary?.invoice_raised ?? 0,
        invoice_pending: summary?.invoice_pending ?? 0,
        invoice_amount_raised: summary?.invoice_amount_raised ?? 0,
        invoice_amount_paid: summary?.invoice_amount_paid ?? 0,
      });

      const totalCount = responseData?.count ?? response?.count ?? 0;
      handleTotal(totalCount);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      showToast({ variant: "error", message: "Failed to fetch assignments" });
      setAssignments([]);
      setAssignmentSummary({
        count: 0,
        invoice_raised: 0,
        invoice_pending: 0,
        invoice_amount_raised: 0,
        invoice_amount_paid: 0,
      });
      handleTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    filters.branch_name,
    filters.client_id,
    filters.invoice_number,
    filters.lan,
    filters.customer_name,
    filters.assignment_from_date,
    filters.assignment_to_date,
    filters.assignment_type,
    filters.invoice_status,
    handleTotal,
    paginationProps.limitPerPage,
  ]);

  useEffect(() => {
    fetchAssignments(currentPage);
  }, [fetchAssignments, currentPage]);

  // Add styles for invoice modal positioning
  useEffect(() => {
    const styleId = 'invoice-modal-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .invoice-details-modal-top.modal-dialog {
          margin-top: 0;
          margin-bottom: auto;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const handlePageChange = (page) => {
    paginationProps.handlePagination(page);
    fetchAssignments(page);
  };

  const handleApplyFilters = (appliedFilters) => {
    // Check if appliedFilters is empty (clearing filters)
    const isEmpty = !appliedFilters || Object.keys(appliedFilters).length === 0;
    
    if (isEmpty) {
      clearFilters();
    } else {
      updateFilters(appliedFilters);
    }
    
    resetPagination();
    setOpenFilterModal(false);
    
    // useEffect will automatically trigger and call fetchAssignments when filters change
  };

  // Setup auto-refresh hook for maintaining filters after CRUD operations
  const { triggerRefetch } = useAutoRefresh(
    useCallback(() => fetchAssignments(currentPage), [fetchAssignments, currentPage]),
    300
  );

  const handleRefetchData = () => {
    triggerRefetch();
  };

  const handleOpenInvoiceDetails = async (item) => {
    setInvoicesLoading(true);
    setInvoicesList(null);
    // open modal by setting a minimal selectedInvoiceDetails object
    setSelectedInvoiceDetails({ case_id: item?.case_id || item?.id || null });

    try {
      const caseId = item?.case_id || item?.id || null;
      if (!caseId) {
        // fallback to showing single item details
        setInvoicesList([{
          invoice_number: item?.invoice_number  || "-",
          invoice_amount: item?.invoice_amount ?? "-",
          invoice_date: item?.invoice_date || item?.batch?.invoice_date || item?.assignment_date || "-",
          pdf_url: item?.pdf_url || item?.batch?.pdf_url || null,
        }]);
        return;
      }

      const resp = await getInvoicesByCaseId(caseId);
      // Response shape: { status: true, message, data: [ ... ] }
      if (resp && resp.status === true && Array.isArray(resp.data)) {
        setInvoicesList(resp.data);
      } else if (resp && Array.isArray(resp.data)) {
        setInvoicesList(resp.data);
      } else if (resp && Array.isArray(resp)) {
        setInvoicesList(resp);
      } else {
        // fallback: show single item
        setInvoicesList([{
          invoice_number: item?.invoice_number || "-",
          invoice_amount: item?.invoice_amount ??  "-",
          invoice_date: item?.invoice_date || item?.batch?.invoice_date ||  "-",
          pdf_url: item?.pdf_url || item?.batch?.pdf_url || null,
        }]);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      showToast({ variant: "error", message: "Failed to fetch invoices" });
      setInvoicesList(null);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      if (!invoice?.pdf_url) {
        showToast({ variant: "warning", message: "PDF URL not available" });
        return;
      }

      console.log("Opening PDF with URL:", invoice.pdf_url);
      setPdfLoading(true);

      // Construct the API URL that serves the PDF
      const apiUrl = `https://api.themedius.ai/dashboard/api/download_media/?url=${encodeURIComponent(invoice.pdf_url)}`;
      console.log("API URL:", apiUrl);

      // Fetch the PDF as a blob
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        // Get the blob from response
        const blob = await response.blob();
        console.log("PDF blob received:", blob);
        
        // Create an object URL from the blob
        const blobUrl = URL.createObjectURL(blob);
        console.log("Blob URL created:", blobUrl);
        
        // Set the URL and show the PDF viewer modal
        setPdfViewerUrl(blobUrl);
        setShowPdfViewer(true);
        showToast({ variant: "success", message: "PDF loaded" });
      } else {
        showToast({ variant: "error", message: "Failed to fetch PDF" });
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
      showToast({ variant: "error", message: "Failed to open PDF" });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfViewer = () => {
    // Clean up the blob URL
    if (pdfViewerUrl) {
      URL.revokeObjectURL(pdfViewerUrl);
    }
    setShowPdfViewer(false);
    setPdfViewerUrl(null);
  };

  const handleCloseInvoiceDetails = () => {
    setSelectedInvoiceDetails(null);
    setInvoicesList(null);
    setInvoicesLoading(false);
  };

  const handleOpenUpdateInvoiceModal = (item) => {
    setSelectedAssignmentForUpdate(item);
    // Pre-fill with existing invoice number
    const currentInvoice = item?.invoice_number || item?.invoice_no || "";
    setNewInvoiceNumbers(currentInvoice);
    setUpdateInvoiceModal(true);
  };

  const handleCloseUpdateInvoiceModal = () => {
    setUpdateInvoiceModal(false);
    setSelectedAssignmentForUpdate(null);
    setNewInvoiceNumbers("");
  };

  const handleUpdateInvoiceNumbers = async () => {
    if (!newInvoiceNumbers.trim()) {
      showToast({ variant: "warning", message: "Please enter at least one invoice number" });
      return;
    }

    setUpdatingInvoice(true);
    try {
      const invoiceNumbers = newInvoiceNumbers
        .split("\n")
        .map((num) => num.trim())
        .filter((num) => num.length > 0);

      const caseId = selectedAssignmentForUpdate?.case_id || selectedAssignmentForUpdate?.id;
      console.log("Selected Assignment for Update:", selectedAssignmentForUpdate);
      console.log("Case ID being sent:", caseId);
      console.log("Invoice Numbers being sent:", invoiceNumbers);
      
      if (!caseId) {
        showToast({ variant: "error", message: "Case ID not found. Please try again." });
        setUpdatingInvoice(false);
        return;
      }

      const response = await updateInvoiceNumberApi(caseId, invoiceNumbers);
      console.log("API Response:", response);

      if (response?.status === true || response?.status === "success") {
        showToast({ variant: "success", message: "Invoice numbers updated successfully" });
        handleCloseUpdateInvoiceModal();
        fetchAssignments(currentPage);
      } else {
        showToast({ variant: "error", message: response?.message || "Failed to update invoice numbers" });
      }
    } catch (error) {
      console.error("Error updating invoice numbers:", error);
      showToast({ variant: "error", message: "Failed to update invoice numbers" });
    } finally {
      setUpdatingInvoice(false);
    }
  };

  const handleRaiseInvoice = async (item) => {
    try {
      const caseId = item?.case_id || item?.id;
      if (!caseId) {
        showToast({ variant: "error", message: "Case ID not found" });
        return;
      }

      // Fetch case details to prefill invoice fields
      const caseResp = await getCase({ id: caseId });
      const respBody = caseResp?.data || caseResp || {};
      const normalizedCaseId = String(caseId);
      const caseRows = Array.isArray(respBody?.data?.results)
        ? respBody.data.results
        : Array.isArray(respBody?.results)
          ? respBody.results
          : Array.isArray(respBody?.data)
            ? respBody.data
            : [];

      let caseObj =
        caseRows.find((row) => {
          const rowId = row?.case_id || row?.id || row?.pk || row?.caseId;
          return String(rowId) === normalizedCaseId;
        }) ||
        caseRows[0] ||
        (respBody && typeof respBody === 'object' ? respBody : null);

      const merged = { ...(item || {}), ...(caseObj || {}) };
console.log("Merged case and assignment data for invoice prefill:", merged);
      // Explicitly map key invoice fields from case data to ensure modal prefills
      merged.lan = merged.lan || merged.lan_number || merged.case_lan || "";

      // Client fields: prefer numeric client_id, fall back to nested client object
      const clientId = merged.client_id || (merged.client && typeof merged.client === 'number' ? merged.client : (merged.client && merged.client.id) || null) || null;
      merged.client_id = clientId || merged.client_id || null;
      merged.client_name = merged.client_name || "";

      // Branch fields
      merged.branch_name = merged.branch_name || merged.branch?.branch_name || merged.branch?.name || merged.batch?.branch_name || merged.branchName || "";

      // Billing fields
      merged.bill_to_name = merged.bill_to_name || merged.billing_name || merged.client_billing_name || merged.client_name || "";
      merged.bill_address = merged.bill_address || merged.billing_address || merged.client_billing_address || merged.address || "";

      // Dates normalization
      if (merged.invoice_date && typeof merged.invoice_date === 'string' && merged.invoice_date.includes('T')) {
        merged.invoice_date = merged.invoice_date.split('T')[0];
      }
      if (merged.effective_dates && Array.isArray(merged.effective_dates)) {
        merged.effective_dates = merged.effective_dates.join(', ');
      }
      if (merged.non_effective_dates && Array.isArray(merged.non_effective_dates)) {
        merged.non_effective_dates = merged.non_effective_dates.join(', ');
      }

      setSelectedAssignmentForRaise(merged);
      setShowRaiseInvoiceModal(true);
    } catch (error) {
      console.error("Error opening raise invoice modal:", error);
      showToast({ variant: "error", message: "Error opening raise invoice modal" });
    }
  };

  const handleConfirmRaiseInvoice = async () => {
    if (!selectedAssignmentForRaise) {
      showToast({ variant: "error", message: "Assignment not found" });
      return;
    }

    setRaisingInvoice(true);
    try {
      const formData = new FormData();
      
      // Add all invoice fields to FormData from selectedAssignmentForRaise
      Object.keys(selectedAssignmentForRaise).forEach((key) => {
        const value = selectedAssignmentForRaise[key];
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      });
      
      // Ensure status is set to Raised
      formData.set('status', 'Raised');

      const response = await fetch(`https://api.themedius.ai/litigation/api/add-litigation-invoice/`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        showToast({ variant: "success", message: "Invoice raised successfully" });
        setShowRaiseInvoiceModal(false);
        setSelectedAssignmentForRaise(null);
        fetchAssignments(currentPage);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast({ variant: "error", message: errorData?.message || "Failed to raise invoice" });
      }
    } catch (error) {
      console.error("Error raising invoice:", error);
      showToast({ variant: "error", message: "Error raising invoice" });
    } finally {
      setRaisingInvoice(false);
    }
  };

  const handleRaiseInvoiceFieldChange = (field, value) => {
    setSelectedAssignmentForRaise((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'client_id' ? { branch_name: "" } : {}),
    }));
  };

  // When branch options load, auto-select first branch if none selected
  useEffect(() => {
    if (!showRaiseInvoiceModal) return;
    if (invoiceBranchOptions && invoiceBranchOptions.length > 0) {
      setSelectedAssignmentForRaise((prev) => {
        if (!prev) return prev;
        if (prev.branch_name && String(prev.branch_name).trim() !== "") return prev;
        return { ...prev, branch_name: invoiceBranchOptions[0].value };
      });
    }
  }, [invoiceBranchOptions, showRaiseInvoiceModal]);

  const handleCloseRaiseInvoiceModal = () => {
    setShowRaiseInvoiceModal(false);
    setSelectedAssignmentForRaise(null);
    setRaisingInvoice(false);
  };

  const fetchInvoiceClients = useCallback(async () => {
    setInvoiceClientsLoading(true);
    try {
      const res = await getClients({ limit: 200 });
      const payload = res?.data || {};
      const clientsList = Array.isArray(payload?.data?.results)
        ? payload.data.results
        : Array.isArray(payload?.results)
          ? payload.results
          : [];

      const options = clientsList
        .map((client) => ({ label: client?.full_name || client?.name || String(client?.id || ""), value: client?.id }))
        .filter((opt) => opt.value);

      setInvoiceClients(options);
    } catch (error) {
      console.error('Error fetching invoice clients:', error);
      setInvoiceClients([]);
    } finally {
      setInvoiceClientsLoading(false);
    }
  }, []);

  const fetchInvoiceBranchOptions = useCallback(async (clientId) => {
    if (!clientId) {
      setInvoiceBranchOptions([]);
      return;
    }

    setInvoiceBranchesLoading(true);
    try {
      const resp = await getInvoiceBranches({ client_id: clientId });
      
      // Handle different response formats
      let results = [];
      
      // If resp.data is an array (new API format returns array of strings)
      if (Array.isArray(resp?.data)) {
        results = resp.data;
      }
      // If resp.data.results is an array (object with results)
      else if (Array.isArray(resp?.data?.results)) {
        results = resp.data.results;
      }
      // If resp.results is an array (legacy format)
      else if (Array.isArray(resp?.results)) {
        results = resp.results;
      }
      // If resp.data.data is an array (nested data)
      else if (Array.isArray(resp?.data?.data)) {
        results = resp.data.data;
      }

      const options = results.map((b) => {
        let name = "";
        if (typeof b === "string") {
          // Branch is already a string (direct branch name)
          name = b;
        } else if (typeof b === "object") {
          // Branch is an object, extract name property
          name = b.branch_name || b.name || (b.branch && (b.branch.branch_name || b.branch.name)) || "";
        }
        return name ? { label: name, value: name } : null;
      }).filter(Boolean);

      setInvoiceBranchOptions(options);
    } catch (error) {
      console.error('Error fetching invoice branches:', error);
      setInvoiceBranchOptions([]);
    } finally {
      setInvoiceBranchesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showRaiseInvoiceModal) fetchInvoiceClients();
  }, [showRaiseInvoiceModal, fetchInvoiceClients]);

  useEffect(() => {
    if (!showRaiseInvoiceModal) return;
    const resolvedClientId = selectedAssignmentForRaise?.client_id || (typeof selectedAssignmentForRaise?.client === 'number' ? selectedAssignmentForRaise.client : null);
    if (resolvedClientId) {
      fetchInvoiceBranchOptions(resolvedClientId);
    }
  }, [showRaiseInvoiceModal, selectedAssignmentForRaise, fetchInvoiceBranchOptions]);

  const summaryCards = [
    { label: "Total Assignments", value: assignmentSummary.count },
    { label: "Invoice Raised", value: assignmentSummary.invoice_raised },
    { label: "Invoice Pending", value: assignmentSummary.invoice_pending },
    { label: "Invoice Amount", value: formatCurrency(assignmentSummary.invoice_amount_raised) },
    { label: "Amount Paid", value: formatCurrency(assignmentSummary.invoice_amount_paid) },
  ];

  return (
    <>
      <div className="bg-white border-bottom px-3 py-3">
        <div className="row w-100 g-2 align-items-center">
          <div className="col-12 col-lg-6 d-flex align-items-center gap-2">
            <FilterButton
              onClick={() => setOpenFilterModal(true)}
              active={hasActiveFilters}
            />
          </div>
        </div>
      </div>

      <div className="p-3">
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h4 className="mb-1">Assignment Summary</h4>
              
            </div>
          </div>

          <div className="row g-3">
            {summaryCards.map((card) => (
              <div className="col-12 col-sm-6 col-xl-2" key={card.label}>
                <div className="border rounded-3 bg-white shadow-sm h-100 p-3">
                  <div className="text-muted small fw-semibold mb-2">{card.label}</div>
                  <div className="fs-5 fw-bold text-dark">{card.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center ">
            <div className="spinner-border text-primary" role="status" aria-hidden="true" />
          </div>
        ) : assignments.length === 0 ? (
          <NoRecord />
        ) : (
          <>
            <div style={{ overflowX: "hidden", overflowY: "visible" }}>
              <table
                style={{
                  width: "100%",
                  tableLayout: "fixed",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
                className="table table-bordered border table-sm"
              >
                <thead className="thead-light">
                  <tr>
                    <th className="p-3 text-left align-top">Client</th>
                    <th className="p-3 text-left align-top">Client Email ID</th>
                    <th className="p-3 text-left align-top" style={{ maxWidth: "100px" }}>Client Contact No</th>
                    <th className="p-3 text-left align-top">Invoice Number</th>
                    <th className="p-3 text-left align-top">LAN</th>
                    <th className="p-3 text-left align-top">Customer Name</th>
                    <th className="p-3 text-left align-top">Branch Name</th>
                    <th className="p-3 text-left align-top">Claim Amount</th>
                    <th className="p-3 text-left align-top">Assignment Date</th>
                    <th className="p-3 text-left align-top">Assignment Type</th>
                    <th className="p-3 text-center align-top" style={{ minWidth: "220px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((item, index) => (
                    <tr key={item.case_id || index} style={{ background: index % 2 === 0 ? "transparent" : "#fbfbfb" }}>
                      <td className="p-3 text-left">{item.client_name || "-"}</td>
                      <td className="p-3 text-left">{item.client_email_id || item.batch?.client_email_id || "-"}</td>
                      <td className="p-3 text-left" style={{ maxWidth: "50px" }}>{item.client_contact_no || item.batch?.client_contact_no || "-"}</td>
                      <td className="p-3 text-left">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-underline fw-semibold align-baseline"
                          onClick={() => handleOpenInvoiceDetails(item)}
                          disabled={!item.invoice_number && !item.invoice_no}
                          title={!item.invoice_number && !item.invoice_no ? "No invoice details available" : "Click to view invoice details"}
                        >
                          {item.invoice_number || item.invoice_no || "-"}
                        </button>
                      </td>
                      <td className="p-3 text-left">{item.lan || item.lan_number || "-"}</td>
                      <td className="p-3 text-left">{item.customer_name || "-"}</td>
                      <td className="p-3 text-left">{item.branch_name || item.batch?.branch_name || "-"}</td>
                      <td className="p-3 text-left">{formatCurrency(item.claim_amount)}</td>
                      <td className="p-3 text-left">{item.assignment_date || item.batch?.assignment_date || "-"}</td>
                      <td className="p-3 text-left">{item.assignment_type || "General"}</td>
                      <td className="p-3 text-center" style={{ minWidth: "220px" }}>
                        <Dropdown>
                          <Dropdown.Toggle variant="primary" className="w-100 text-start py-2 select-style">
                            Action <img src={Arrow} alt="Sort" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu 
                            className="select-style-dropdown" 
                            style={{ 
                              zIndex: 2000,
                              minWidth: "220px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                            }}
                          >
                            <Dropdown.Item 
                              onClick={() => handleRaiseInvoice(item)}
                              style={{ padding: "10px 16px", fontSize: "14px" }}
                            >
                              Raise Invoice
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => handleOpenUpdateInvoiceModal(item)}
                              style={{ padding: "10px 16px", fontSize: "14px" }}
                            >
                              Update Invoice Number
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-center mt-2">
              <p className="text-muted mb-0">Showing {assignments.length} records | Total records: {total}</p>
            </div>

            <div className="d-flex justify-content-center mt-2">
              <Pagination
                current={currentPage}
                total={total}
                onPageChange={handlePageChange}
                totalPages={paginationProps.pageTotal}
              />
            </div>
          </>
        )}
      </div>

      <AssignmentsFilterModal
        show={openFilterModal}
        close={() => setOpenFilterModal(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
        assignmentTypeOptions={assignmentTypes}
        loadingAssignmentTypes={loadingAssignmentTypes}
      />

      <Modal show={updateInvoiceModal} onHide={handleCloseUpdateInvoiceModal}  size="lg">
        <Modal.Header closeButton className="border-bottom bg-light">
          <Modal.Title className="fw-bold text-dark">Update Invoice Number</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
         
          <div className="mb-3">
            <label className="form-label fw-semibold mb-2"> Invoice Number 1,Invoice Number 2,.. (comma-separated)</label>
            <textarea
              className="form-control"
              placeholder="Enter invoice numbers here (comma-separated):"
              rows="6"
              value={newInvoiceNumbers}
              onChange={(e) => setNewInvoiceNumbers(e.target.value)}
              style={{ fontFamily: "monospace", lineHeight: "1.8" }}
            />
           
          </div>

         
        </Modal.Body>
        <Modal.Footer className="border-top bg-light">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleCloseUpdateInvoiceModal}
            disabled={updatingInvoice}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleUpdateInvoiceNumbers}
            disabled={updatingInvoice || !newInvoiceNumbers.trim()}
          >
            {updatingInvoice ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPdfViewer} onHide={handleClosePdfViewer} size="lg"  scrollable>
        <Modal.Header closeButton className="border-bottom bg-light">
          <Modal.Title className="fw-bold text-dark">PDF Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-5">
          {pdfViewerUrl ? (
            <embed
              src={pdfViewerUrl}
              type="application/pdf"
              width="100%"
              height="800px"
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "600px" }}>
              <div className="text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading PDF...</p>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!selectedInvoiceDetails} onHide={handleCloseInvoiceDetails} size="lg" dialogClassName="invoice-details-modal-top">
        <Modal.Header closeButton className="border-bottom bg-light">
          <Modal.Title className="fw-bold text-dark">Invoice Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div>
            {invoicesLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status" aria-hidden="true" />
              </div>
            ) : Array.isArray(invoicesList) && invoicesList.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover table-sm align-middle">
                  <thead>
                    <tr className="table-light border-bottom">
                      <th className="fw-bold text-dark py-3">Invoice Number</th>
                      <th className="fw-bold text-dark py-3">Invoice Amount</th>
                      <th className="fw-bold text-dark py-3">Invoice Date</th>
                      <th className="fw-bold text-dark py-3 text-center">view pdf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesList.map((inv, idx) => (
                      <tr key={inv.invoice_number || idx} className="border-bottom">
                        <td className="fw-semibold text-dark">{inv.invoice_number || inv.invoice_no || "-"}</td>
                        <td className="fw-semibold text-success">{formatCurrency(inv.invoice_amount)}</td>
                        <td className="text-muted">{inv.invoice_date || "-"}</td>
                        <td className="text-center">
                          <button 
                            type="button" 
                            className="btn btn-sm btn-outline-primary fw-semibold" 
                            onClick={() => {
                              console.log("View PDF clicked, invoice object:", inv);
                              handleDownloadPdf(inv);
                            }}
                            disabled={!inv.pdf_url}
                            title={!inv.pdf_url ? "PDF URL not available" : "View PDF"}
                          >
                            <i className="bi bi-download me-1" /> View PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info text-center mb-0">No invoices found.</div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <InvoiceDetailsModal
        show={showRaiseInvoiceModal}
        mode={"add"}
        invoice={selectedAssignmentForRaise}
        formData={selectedAssignmentForRaise}
        clientOptions={invoiceClients}
        branchOptions={invoiceBranchOptions}
        clientsLoading={invoiceClientsLoading}
        branchesLoading={invoiceBranchesLoading}
        clientNameReadOnly
        branchNameReadOnly
        onClose={handleCloseRaiseInvoiceModal}
        onSave={handleConfirmRaiseInvoice}
        onFieldChange={handleRaiseInvoiceFieldChange}
        saving={raisingInvoice}
      />
    </>
  );
}
